import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from '../lib/i18n';
import { turkishIncludes } from '../lib/turkishSearch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Languages as LanguagesIcon, 
  Search, 
  Edit, 
  Save, 
  Plus,
  Download,
  Upload,
  RefreshCw,
  Globe,
  Check,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileJson,
  AlertTriangle,
  Database,
  CheckCircle,
  ScanSearch,
  Code,
  FileWarning
} from 'lucide-react';
import { translations as i18nTranslations, useI18nStore } from '../lib/i18n';
import api from '../lib/api';

export const Languages = () => {
  const { t } = useTranslation();
  const { language } = useI18nStore();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLang, setSelectedLang] = useState(null); // null = all, 'tr'/'en'/'nl'/'fr' = filter by missing
  const [selectedNamespace, setSelectedNamespace] = useState('all');
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, key: '', values: {} });
  const [addDialog, setAddDialog] = useState({ open: false, key: '', values: { tr: '', en: '', nl: '', fr: '' } });
  const [importDialog, setImportDialog] = useState({ open: false, analysis: null, file: null, mode: 'merge' });
  const [syncDialog, setSyncDialog] = useState({ open: false, status: null });
  const [translations, setTranslations] = useState({});
  const [customTranslations, setCustomTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('translations');
  
  // Missing keys scan state
  const [isScanning, setIsScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [scanFilterNs, setScanFilterNs] = useState('all');
  const [scanFilterLang, setScanFilterLang] = useState('all');
  const [scanSearchTerm, setScanSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Sorting state
  const [sortField, setSortField] = useState('key');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
  ];

  // Get nested value from object
  const getNestedValue = useCallback((obj, path) => {
    if (!obj) return undefined;
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }, []);

  // Load translations from backend + default
  useEffect(() => {
    loadTranslations();
    checkSyncStatus();
    // Şub 09, 2026 — Sayfa açılışında sessizce eksik anahtar taraması yap
    // (badge'i göstermek için). Hata olursa görsel etki yok.
    const t = setTimeout(() => {
      api.post('/translations/scan-missing', { translations: i18nTranslations })
        .then((r) => { if (r.data?.success) setScanResults(r.data.data); })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const loadTranslations = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/translations');
      if (response.data.success) {
        setCustomTranslations(response.data.data);
      }
      setTranslations(i18nTranslations);
    } catch (error) {
      console.error('Failed to load translations:', error);
      setTranslations(i18nTranslations);
    } finally {
      setIsLoading(false);
    }
  };

  // Check sync status
  const checkSyncStatus = async () => {
    try {
      const response = await api.get('/translations/sync-status');
      if (response.data.success) {
        setSyncStatus(response.data.data);
      }
    } catch (error) {
      console.error('Failed to check sync status:', error);
    }
  };

  // Sync base translations to DB
  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const response = await api.post('/translations/sync', {
        translations: i18nTranslations
      });
      
      if (response.data.success) {
        const stats = response.data.stats;
        toast.success(t('languages.syncComplete'), {
          description: t('languages.syncCompleteDesc').replace('{added}', stats.added).replace('{skipped}', stats.skipped).replace('{updated}', stats.updated || 0)
        });
        setSyncDialog({ open: false, status: null });
        // Reload translations and status
        await loadTranslations();
        await checkSyncStatus();
        // Refresh i18n store
        const { refreshTranslations } = useI18nStore.getState();
        await refreshTranslations();
      }
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error(t('languages.syncFailed'));
    } finally {
      setIsSyncing(false);
    }
  };

  // Flatten translations for display
  const flattenedTranslations = useMemo(() => {
    const result = [];
    const processedKeys = new Set();
    
    const collectKeys = (obj, prefix = '') => {
      for (const key in obj) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          collectKeys(obj[key], fullKey);
        } else if (!processedKeys.has(fullKey)) {
          processedKeys.add(fullKey);
          result.push({
            key: fullKey,
            namespace: prefix.split('.')[0] || 'root',
            value: obj[key],
            translations: {
              tr: getNestedValue(customTranslations.tr, fullKey) || getNestedValue(i18nTranslations.tr, fullKey),
              en: getNestedValue(customTranslations.en, fullKey) || getNestedValue(i18nTranslations.en, fullKey),
              nl: getNestedValue(customTranslations.nl, fullKey) || getNestedValue(i18nTranslations.nl, fullKey),
              fr: getNestedValue(customTranslations.fr, fullKey) || getNestedValue(i18nTranslations.fr, fullKey),
            }
          });
        }
      }
    };
    
    languages.forEach(lang => {
      if (customTranslations[lang.code]) {
        collectKeys(customTranslations[lang.code]);
      }
      if (i18nTranslations[lang.code]) {
        collectKeys(i18nTranslations[lang.code]);
      }
    });
    
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTranslations, getNestedValue]);

  // Get unique namespaces
  const namespaces = useMemo(() => {
    const ns = new Set(flattenedTranslations.map(t => t.namespace));
    return ['all', ...Array.from(ns).sort()];
  }, [flattenedTranslations]);

  // Check if translation is missing
  const isMissing = useCallback((value) => !value || value === '', []);

  // Check if translation key is complete (has all languages)
  const isComplete = useCallback((item) => {
    return languages.every(lang => !isMissing(item.translations[lang.code]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMissing]);

  // Filter and sort translations
  const filteredAndSortedTranslations = useMemo(() => {
    let filtered = flattenedTranslations.filter(item => {
      const matchesSearch = searchTerm === '' || 
        turkishIncludes(item.key, searchTerm) ||
        Object.values(item.translations).some(v => 
          v && turkishIncludes(v, searchTerm)
        );
      
      const matchesNamespace = selectedNamespace === 'all' || item.namespace === selectedNamespace;
      
      // Filter by missing for specific language (when card is clicked)
      let matchesLangMissing = true;
      if (selectedLang) {
        matchesLangMissing = isMissing(item.translations[selectedLang]);
      }
      
      // General missing filter
      const hasMissing = !isComplete(item);
      const matchesMissing = !showOnlyMissing || hasMissing;
      
      return matchesSearch && matchesNamespace && matchesMissing && matchesLangMissing;
    });
    
    // Sort
    filtered.sort((a, b) => {
      let compareA, compareB;
      
      if (sortField === 'key') {
        compareA = a.key.toLowerCase();
        compareB = b.key.toLowerCase();
      } else if (sortField === 'namespace') {
        compareA = a.namespace.toLowerCase();
        compareB = b.namespace.toLowerCase();
      } else if (['tr', 'en', 'nl', 'fr'].includes(sortField)) {
        compareA = (a.translations[sortField] || '').toLowerCase();
        compareB = (b.translations[sortField] || '').toLowerCase();
      } else {
        return 0;
      }
      
      if (sortDirection === 'asc') {
        return compareA.localeCompare(compareB);
      } else {
        return compareB.localeCompare(compareA);
      }
    });
    
    return filtered;
  }, [flattenedTranslations, searchTerm, selectedNamespace, showOnlyMissing, selectedLang, isComplete, isMissing, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedTranslations.length / pageSize);
  const paginatedTranslations = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedTranslations.slice(start, start + pageSize);
  }, [filteredAndSortedTranslations, currentPage, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedNamespace, showOnlyMissing, selectedLang, pageSize]);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Handle card click - filter by missing translations for that language
  const handleCardClick = (langCode) => {
    if (selectedLang === langCode) {
      setSelectedLang(null);
      setShowOnlyMissing(false);
    } else {
      setSelectedLang(langCode);
      setShowOnlyMissing(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (item) => {
    setEditDialog({
      open: true,
      key: item.key,
      values: { ...item.translations }
    });
  };

  // Save translation (edit existing)
  const saveTranslation = async () => {
    const { key, values } = editDialog;
    
    setIsSaving(true);
    try {
      const response = await api.put('/translations/update', {
        key: key,
        values: values
      });
      
      if (response.data.success) {
        toast.success(t('languages.translationSaved'));
        setEditDialog({ open: false, key: '', values: {} });
        await loadTranslations();
        const { refreshTranslations } = useI18nStore.getState();
        await refreshTranslations();
      }
    } catch (error) {
      console.error('Failed to save translation:', error);
      const message = error.response?.data?.detail || t('languages.translationSaveFailed');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Add new translation key
  const addNewTranslation = async () => {
    const { key, values } = addDialog;
    
    if (!key.trim()) {
      toast.error(t('languages.keyNameRequired'));
      return;
    }
    
    const keyExists = flattenedTranslations.some(t => t.key === key.trim());
    if (keyExists) {
      toast.error(t('languages.keyAlreadyExists'));
      return;
    }
    
    const hasValue = Object.values(values).some(v => v.trim());
    if (!hasValue) {
      toast.error(t('languages.atLeastOneValue'));
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await api.post('/translations/add-key', {
        key: key.trim(),
        values: values
      });
      
      if (response.data.success) {
        toast.success(t('languages.newKeyAdded'));
        setAddDialog({ open: false, key: '', values: { tr: '', en: '', nl: '', fr: '' } });
        await loadTranslations();
        const { refreshTranslations } = useI18nStore.getState();
        await refreshTranslations();
      }
    } catch (error) {
      console.error('Failed to add translation:', error);
      const message = error.response?.data?.detail || t('languages.addKeyFailed');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Export translations (all merged)
  const exportTranslations = () => {
    // Merge base with custom
    const merged = {
      tr: { ...i18nTranslations.tr, ...customTranslations.tr },
      en: { ...i18nTranslations.en, ...customTranslations.en },
      nl: { ...i18nTranslations.nl, ...customTranslations.nl },
      fr: { ...i18nTranslations.fr, ...customTranslations.fr },
    };
    
    const data = JSON.stringify(merged, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `translations_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('languages.exported'));
  };

  // Handle file selection for import
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    event.target.value = ''; // Reset file input
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        // Validate structure
        if (!data.tr && !data.en && !data.nl && !data.fr) {
          toast.error(t('languages.invalidFileFormat'));
          return;
        }
        
        setIsAnalyzing(true);
        
        // Analyze the import
        try {
          const response = await api.post('/translations/analyze-import', {
            translations: data,
            mode: 'merge'
          });
          
          if (response.data.success) {
            setImportDialog({
              open: true,
              analysis: response.data.analysis,
              file: data,
              mode: 'merge'
            });
          }
        } catch (error) {
          console.error('Analysis failed:', error);
          const errorMsg = error.response?.data?.detail || error.message || t('languages.analysisFailed');
          toast.error(`${t('languages.analysisFailed')}: ${errorMsg}`);
        } finally {
          setIsAnalyzing(false);
        }
        
      } catch (err) {
        toast.error(t('languages.invalidJson'));
      }
    };
    reader.readAsText(file);
  };

  // Execute import
  const executeImport = async () => {
    if (!importDialog.file) return;
    
    setIsImporting(true);
    try {
      const response = await api.post('/translations/bulk-import', {
        translations: importDialog.file,
        mode: importDialog.mode
      });
      
      if (response.data.success) {
        const stats = response.data.stats;
        toast.success(t('languages.importComplete'), {
          description: t('languages.importCompleteDesc').replace('{added}', stats.added).replace('{updated}', stats.updated).replace('{skipped}', stats.skipped)
        });
        setImportDialog({ open: false, analysis: null, file: null, mode: 'merge' });
        await loadTranslations();
        const { refreshTranslations } = useI18nStore.getState();
        await refreshTranslations();
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(t('languages.importFailed'));
    } finally {
      setIsImporting(false);
    }
  };

  // Reset to defaults
  const resetToDefaults = () => {
    const { clearCache } = useI18nStore.getState();
    clearCache();
    setTranslations(i18nTranslations);
    setCustomTranslations({});
    toast.success(t('languages.cacheCleared'));
  };

  // Scan for missing translations
  const scanMissingKeys = async () => {
    setIsScanning(true);
    try {
      const response = await api.post('/translations/scan-missing', {
        translations: i18nTranslations
      });
      if (response.data.success) {
        setScanResults(response.data.data);
        toast.success(
          t('languages.scanComplete') || 'Tarama tamamlandı',
          { description: `${response.data.data.undefined_in_code.count} tanımsız, ${response.data.data.cross_language_missing.count} diller arası eksik` }
        );
      }
    } catch (error) {
      console.error('Scan failed:', error);
      toast.error(t('languages.scanFailed') || 'Tarama başarısız');
    } finally {
      setIsScanning(false);
    }
  };

  // Filtered scan results
  const filteredUndefinedKeys = useMemo(() => {
    if (!scanResults) return [];
    let items = scanResults.undefined_in_code.items;
    if (scanFilterNs !== 'all') {
      items = items.filter(i => i.namespace === scanFilterNs);
    }
    if (scanSearchTerm) {
      items = items.filter(i => turkishIncludes(i.key, scanSearchTerm));
    }
    return items;
  }, [scanResults, scanFilterNs, scanSearchTerm]);

  const filteredCrossLangKeys = useMemo(() => {
    if (!scanResults) return [];
    let items = scanResults.cross_language_missing.items;
    if (scanFilterNs !== 'all') {
      items = items.filter(i => i.namespace === scanFilterNs);
    }
    if (scanFilterLang !== 'all') {
      items = items.filter(i => i.missing_in.includes(scanFilterLang));
    }
    if (scanSearchTerm) {
      items = items.filter(i => turkishIncludes(i.key, scanSearchTerm));
    }
    return items;
  }, [scanResults, scanFilterNs, scanFilterLang, scanSearchTerm]);

  const scanNamespaces = useMemo(() => {
    if (!scanResults) return ['all'];
    const ns = new Set();
    scanResults.undefined_in_code.items.forEach(i => ns.add(i.namespace));
    scanResults.cross_language_missing.items.forEach(i => ns.add(i.namespace));
    return ['all', ...Array.from(ns).sort()];
  }, [scanResults]);

  // Count missing translations
  const missingCount = useMemo(() => {
    const counts = {};
    languages.forEach(lang => {
      counts[lang.code] = flattenedTranslations.filter(t => isMissing(t.translations[lang.code])).length;
    });
    return counts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flattenedTranslations, isMissing]);

  // Total missing count
  const totalMissingCount = useMemo(() => {
    return flattenedTranslations.filter(t => !isComplete(t)).length;
  }, [flattenedTranslations, isComplete]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="languages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('nav.languages') || 'Diller'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('languages.subtitle')}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* Sync Button - Show warning if not synced */}
          <Button 
            variant={syncStatus?.is_synced ? "outline" : "default"}
            onClick={() => setSyncDialog({ open: true, status: syncStatus })}
            className={`gap-2 ${!syncStatus?.is_synced ? 'bg-orange-500 hover:bg-orange-600 animate-pulse' : 'border-green-300 text-green-600'}`}
            disabled={isSyncing}
          >
            {isSyncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : syncStatus?.is_synced ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <Database className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">
              {syncStatus?.is_synced ? t('languages.synced') : t('languages.syncToDb')}
            </span>
          </Button>
          
          <Button onClick={() => setAddDialog({ open: true, key: '', values: { tr: '', en: '', nl: '', fr: '' } })} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">{t('languages.newKey')}</span>
          </Button>
          
          <Button variant="outline" onClick={exportTranslations} className="gap-2">
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{t('languages.export')}</span>
          </Button>
          
          <label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isAnalyzing}
            />
            <Button variant="outline" className="gap-2" asChild disabled={isAnalyzing}>
              <span>
                {isAnalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span className="hidden sm:inline">{isAnalyzing ? t('languages.analyzing') : t('languages.import')}</span>
              </span>
            </Button>
          </label>
          
          <Button variant="outline" onClick={resetToDefaults} className="gap-2 text-amber-600 hover:text-amber-700">
            <RefreshCw className="h-4 w-4" />
            <span className="hidden sm:inline">{t('languages.reset')}</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v);
          // Şub 09, 2026 — Eksik Tespit sekmesine geçildiğinde otomatik tara
          if (v === 'scan' && !scanResults && !isScanning) {
            scanMissingKeys();
          }
        }}
        className="space-y-4"
      >
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="translations" className="gap-2" data-testid="tab-translations">
            <LanguagesIcon className="h-4 w-4" />
            {t('languages.translationsTab') || 'Çeviriler'}
          </TabsTrigger>
          <TabsTrigger value="scan" className="gap-2" data-testid="tab-scan-missing">
            <ScanSearch className="h-4 w-4" />
            {t('languages.scanTab') || 'Eksik Tespit'}
            {scanResults && (scanResults.undefined_in_code.count + scanResults.cross_language_missing.count) > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs h-5 min-w-5 px-1">
                {scanResults.undefined_in_code.count + scanResults.cross_language_missing.count}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Translations Tab */}
        <TabsContent value="translations" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {languages.map(lang => (
          <Card 
            key={lang.code} 
            className={`cursor-pointer transition-all hover:shadow-md ${
              selectedLang === lang.code 
                ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : ''
            }`}
            onClick={() => handleCardClick(lang.code)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{lang.flag}</span>
                  <div>
                    <p className="font-medium">{lang.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{lang.code.toUpperCase()}</p>
                  </div>
                </div>
                {missingCount[lang.code] > 0 ? (
                  <Badge 
                    variant="destructive" 
                    className={`text-xs cursor-pointer ${selectedLang === lang.code ? 'ring-2 ring-white' : ''}`}
                  >
                    {missingCount[lang.code]} {t('languages.missing')}
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                    <Check className="h-3 w-3 mr-1" />
                    {t('languages.complete')}
                  </Badge>
                )}
              </div>
              {selectedLang === lang.code && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  {t('languages.filteringMissing')}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex-1 w-full sm:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('languages.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={selectedNamespace} onValueChange={setSelectedNamespace}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('languages.selectNamespace')} />
              </SelectTrigger>
              <SelectContent>
                {namespaces.map(ns => (
                  <SelectItem key={ns} value={ns}>
                    {ns === 'all' ? t('languages.allNamespaces') : ns}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 {t('languages.perPageSuffix')}</SelectItem>
                <SelectItem value="25">25 {t('languages.perPageSuffix')}</SelectItem>
                <SelectItem value="50">50 {t('languages.perPageSuffix')}</SelectItem>
                <SelectItem value="100">100 {t('languages.perPageSuffix')}</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant={showOnlyMissing ? "default" : "outline"}
              onClick={() => {
                setShowOnlyMissing(!showOnlyMissing);
                setSelectedLang(null);
              }}
              className={`gap-2 ${showOnlyMissing ? 'bg-red-600 hover:bg-red-700' : ''}`}
            >
              <AlertCircle className="h-4 w-4" />
              {t('languages.allMissing')} ({totalMissingCount})
            </Button>
            
            {selectedLang && (
              <Button
                variant="outline"
                onClick={() => setSelectedLang(null)}
                className="gap-2 text-blue-600"
              >
                <RefreshCw className="h-4 w-4" />
                {t('languages.clearFilter')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Translations Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <LanguagesIcon className="h-5 w-5 text-blue-600" />
                {t('languages.translationsTitle')}
              </CardTitle>
              <CardDescription>
                {filteredAndSortedTranslations.length} {t('languages.translationsFound')}
                {selectedLang && <span className="text-blue-600 ml-2">({languages.find(l => l.code === selectedLang)?.name} {t('languages.missingFor')})</span>}
                {showOnlyMissing && !selectedLang && <span className="text-red-600 ml-2">({t('languages.allMissingLabel')})</span>}
              </CardDescription>
            </div>
            
            {/* Pagination Info */}
            <div className="text-sm text-slate-500">
              {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredAndSortedTranslations.length)} / {filteredAndSortedTranslations.length}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-auto">
              <Table>
                <TableHeader className="bg-slate-50 dark:bg-slate-800">
                  <TableRow>
                    <TableHead 
                      className="w-[280px] cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                      onClick={() => handleSort('key')}
                    >
                      <div className="flex items-center">
                        {t('languages.keyColumn')}
                        {getSortIcon('key')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                      onClick={() => handleSort('tr')}
                    >
                      <div className="flex items-center">
                        🇹🇷 TR
                        {getSortIcon('tr')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                      onClick={() => handleSort('en')}
                    >
                      <div className="flex items-center">
                        🇬🇧 EN
                        {getSortIcon('en')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                      onClick={() => handleSort('nl')}
                    >
                      <div className="flex items-center">
                        🇳🇱 NL
                        {getSortIcon('nl')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                      onClick={() => handleSort('fr')}
                    >
                      <div className="flex items-center">
                        🇫🇷 FR
                        {getSortIcon('fr')}
                      </div>
                    </TableHead>
                    <TableHead className="w-[70px]">{t('languages.actionColumn')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTranslations.map((item, index) => (
                    <TableRow key={item.key} className={index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-800/50'}>
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-2">
                          {!isComplete(item) && (
                            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                          )}
                          <span className="truncate max-w-[230px]" title={item.key}>
                            {item.key}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className={`text-sm ${isMissing(item.translations.tr) ? 'text-red-500 italic bg-red-50 dark:bg-red-900/20' : ''}`}>
                        <span className="truncate block max-w-[140px]" title={item.translations.tr}>
                          {item.translations.tr || t('languages.missingValue')}
                        </span>
                      </TableCell>
                      <TableCell className={`text-sm ${isMissing(item.translations.en) ? 'text-red-500 italic bg-red-50 dark:bg-red-900/20' : ''}`}>
                        <span className="truncate block max-w-[140px]" title={item.translations.en}>
                          {item.translations.en || t('languages.missingValue')}
                        </span>
                      </TableCell>
                      <TableCell className={`text-sm ${isMissing(item.translations.nl) ? 'text-red-500 italic bg-red-50 dark:bg-red-900/20' : ''}`}>
                        <span className="truncate block max-w-[140px]" title={item.translations.nl}>
                          {item.translations.nl || t('languages.missingValue')}
                        </span>
                      </TableCell>
                      <TableCell className={`text-sm ${isMissing(item.translations.fr) ? 'text-red-500 italic bg-red-50 dark:bg-red-900/20' : ''}`}>
                        <span className="truncate block max-w-[140px]" title={item.translations.fr}>
                          {item.translations.fr || t('languages.missingValue')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(item)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedTranslations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        {t('languages.noResults')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-slate-500">
                {t('languages.pageLabel')} {currentPage} / {totalPages}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1 mx-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Scan Missing Keys Tab */}
        <TabsContent value="scan" className="space-y-4">
          {/* Scan Action Bar */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <ScanSearch className="h-5 w-5 text-blue-600" />
                    {t('languages.scanTitle') || 'Eksik Çeviri Anahtarı Tespiti'}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {t('languages.scanDescription') || 'Kaynak kodda kullanılan ama tanımlanmayan çeviri anahtarlarını ve diller arası eksiklikleri tespit eder.'}
                  </p>
                </div>
                <Button
                  onClick={scanMissingKeys}
                  disabled={isScanning}
                  className="gap-2 bg-blue-600 hover:bg-blue-700 shrink-0"
                  data-testid="scan-missing-btn"
                >
                  {isScanning ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ScanSearch className="h-4 w-4" />
                  )}
                  {isScanning ? (t('languages.scanning') || 'Taranıyor...') : (t('languages.scanBtn') || 'Tara')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Scan Results */}
          {scanResults && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-blue-200 dark:border-blue-800">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{scanResults.scanned_keys_count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('languages.scanKeysScanned') || 'Kodda Taranan'}</p>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200 dark:border-emerald-800">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{scanResults.defined_keys_count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('languages.scanKeysDefined') || 'i18n Tanımlı'}</p>
                  </CardContent>
                </Card>
                <Card className={`border-2 ${scanResults.undefined_in_code.count > 0 ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10' : 'border-emerald-300'}`}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-2xl font-bold ${scanResults.undefined_in_code.count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {scanResults.undefined_in_code.count}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('languages.scanUndefined') || 'Tanımsız Anahtar'}</p>
                  </CardContent>
                </Card>
                <Card className={`border-2 ${scanResults.cross_language_missing.count > 0 ? 'border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10' : 'border-emerald-300'}`}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-2xl font-bold ${scanResults.cross_language_missing.count > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {scanResults.cross_language_missing.count}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('languages.scanCrossLang') || 'Diller Arası Eksik'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Filters for scan results */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-3 items-center">
                    <div className="flex-1 w-full sm:w-auto">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder={t('languages.scanSearchPlaceholder') || 'Anahtar ara...'}
                          value={scanSearchTerm}
                          onChange={(e) => setScanSearchTerm(e.target.value)}
                          className="pl-10"
                          data-testid="scan-search-input"
                        />
                      </div>
                    </div>
                    <Select value={scanFilterNs} onValueChange={setScanFilterNs}>
                      <SelectTrigger className="w-[180px]" data-testid="scan-filter-ns">
                        <SelectValue placeholder="Namespace" />
                      </SelectTrigger>
                      <SelectContent>
                        {scanNamespaces.map(ns => (
                          <SelectItem key={ns} value={ns}>
                            {ns === 'all' ? (t('languages.allNamespaces') || 'Tüm Namespace') : ns}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={scanFilterLang} onValueChange={setScanFilterLang}>
                      <SelectTrigger className="w-[150px]" data-testid="scan-filter-lang">
                        <SelectValue placeholder="Dil" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('languages.allLanguages') || 'Tüm Diller'}</SelectItem>
                        {languages.map(lang => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.flag} {lang.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Undefined in Code */}
              {scanResults.undefined_in_code.count > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Code className="h-5 w-5 text-red-500" />
                      {t('languages.undefinedInCode') || 'Kodda Kullanılıp Tanımlanmayan Anahtarlar'}
                      <Badge variant="destructive" className="ml-2">{filteredUndefinedKeys.length}</Badge>
                    </CardTitle>
                    <CardDescription>
                      {t('languages.undefinedInCodeDesc') || 'Bu anahtarlar kaynak kodda t() ile çağrılıyor ancak hiçbir dilde tanımlı değil.'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-[400px] overflow-auto">
                        <Table>
                          <TableHeader className="bg-red-50 dark:bg-red-900/20 sticky top-0">
                            <TableRow>
                              <TableHead className="w-[60px]">#</TableHead>
                              <TableHead>{t('languages.keyColumn') || 'Anahtar'}</TableHead>
                              <TableHead className="w-[150px]">Namespace</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredUndefinedKeys.map((item, idx) => (
                              <TableRow key={item.key}>
                                <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                                <TableCell className="font-mono text-sm text-red-700 dark:text-red-400">{item.key}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs">{item.namespace}</Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                            {filteredUndefinedKeys.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={3} className="text-center py-6 text-slate-400">
                                  {t('languages.noResults') || 'Sonuç bulunamadı'}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Cross-Language Missing */}
              {scanResults.cross_language_missing.count > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileWarning className="h-5 w-5 text-amber-500" />
                      {t('languages.crossLangMissing') || 'Diller Arası Eksik Anahtarlar'}
                      <Badge className="bg-amber-100 text-amber-700 ml-2">{filteredCrossLangKeys.length}</Badge>
                    </CardTitle>
                    <CardDescription>
                      {t('languages.crossLangMissingDesc') || 'Bu anahtarlar bazı dillerde tanımlı ama diğerlerinde eksik.'}
                    </CardDescription>
                    {/* Per-language summary */}
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {languages.map(lang => {
                        const count = scanResults.cross_language_missing.by_language[lang.code] || 0;
                        return count > 0 ? (
                          <Badge key={lang.code} variant="outline" className="text-xs gap-1 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                            onClick={() => setScanFilterLang(scanFilterLang === lang.code ? 'all' : lang.code)}
                          >
                            {lang.flag} {lang.code.toUpperCase()}: {count} {t('languages.missing') || 'eksik'}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-[400px] overflow-auto">
                        <Table>
                          <TableHeader className="bg-amber-50 dark:bg-amber-900/20 sticky top-0">
                            <TableRow>
                              <TableHead className="w-[60px]">#</TableHead>
                              <TableHead>{t('languages.keyColumn') || 'Anahtar'}</TableHead>
                              <TableHead className="w-[150px]">Namespace</TableHead>
                              <TableHead>{t('languages.missingInLangs') || 'Eksik Diller'}</TableHead>
                              <TableHead>{t('languages.presentInLangs') || 'Mevcut Diller'}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredCrossLangKeys.map((item, idx) => (
                              <TableRow key={item.key}>
                                <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                                <TableCell className="font-mono text-sm">{item.key}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs">{item.namespace}</Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1 flex-wrap">
                                    {item.missing_in.map(lang => (
                                      <Badge key={lang} variant="destructive" className="text-xs">
                                        {languages.find(l => l.code === lang)?.flag} {lang.toUpperCase()}
                                      </Badge>
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1 flex-wrap">
                                    {item.present_in.map(lang => (
                                      <Badge key={lang} className="bg-emerald-100 text-emerald-700 text-xs">
                                        {languages.find(l => l.code === lang)?.flag} {lang.toUpperCase()}
                                      </Badge>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                            {filteredCrossLangKeys.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center py-6 text-slate-400">
                                  {t('languages.noResults') || 'Sonuç bulunamadı'}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* All clear message */}
              {scanResults.undefined_in_code.count === 0 && scanResults.cross_language_missing.count === 0 && (
                <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                    <h3 className="font-semibold text-emerald-700 dark:text-emerald-300 text-lg">
                      {t('languages.allKeysComplete') || 'Tüm Anahtarlar Tamamlandı!'}
                    </h3>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                      {t('languages.allKeysCompleteDesc') || 'Kodda kullanılan tüm çeviri anahtarları tüm dillerde tanımlı.'}
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* No scan yet */}
          {!scanResults && !isScanning && (
            <Card className="border-dashed border-2">
              <CardContent className="p-12 text-center">
                <ScanSearch className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                <h3 className="font-medium text-slate-500 dark:text-slate-400">
                  {t('languages.scanPrompt') || 'Eksik çeviri anahtarlarını tespit etmek için tarama başlatın'}
                </h3>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                  {t('languages.scanPromptDesc') || 'Kaynak kod dosyaları taranarak kullanılan anahtarlar i18n tanımları ile karşılaştırılacaktır.'}
                </p>
                <Button onClick={scanMissingKeys} className="mt-4 gap-2 bg-blue-600 hover:bg-blue-700" data-testid="scan-missing-btn-empty">
                  <ScanSearch className="h-4 w-4" />
                  {t('languages.scanBtn') || 'Taramayı Başlat'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Scanning state */}
          {isScanning && (
            <Card>
              <CardContent className="p-12 text-center">
                <Loader2 className="h-10 w-10 text-blue-500 mx-auto mb-3 animate-spin" />
                <h3 className="font-medium text-slate-600 dark:text-slate-300">
                  {t('languages.scanningInProgress') || 'Kaynak kodlar taranıyor...'}
                </h3>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      <Dialog open={editDialog.open} onOpenChange={(v) => !v && setEditDialog({ open: false, key: '', values: {} })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-600" />
              {t('languages.editTitle')}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs bg-slate-100 dark:bg-slate-700 p-2 rounded mt-2">
              {editDialog.key}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {languages.map(lang => (
              <div key={lang.code}>
                <Label className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{lang.flag}</span>
                  {lang.name}
                  {isMissing(editDialog.values[lang.code]) && (
                    <Badge variant="destructive" className="text-xs ml-2">{t('languages.missingBadge')}</Badge>
                  )}
                </Label>
                <Input
                  value={editDialog.values[lang.code] || ''}
                  onChange={(e) => setEditDialog(prev => ({
                    ...prev,
                    values: { ...prev.values, [lang.code]: e.target.value }
                  }))}
                  placeholder={`${lang.name} ${t('languages.translationPlaceholder')}`}
                />
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, key: '', values: {} })} disabled={isSaving}>
              {t('languages.cancel')}
            </Button>
            <Button onClick={saveTranslation} className="gap-2" disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isSaving ? t('languages.saving') : t('languages.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Key Dialog */}
      <Dialog open={addDialog.open} onOpenChange={(v) => !v && setAddDialog({ open: false, key: '', values: { tr: '', en: '', nl: '', fr: '' } })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-600" />
              {t('languages.addTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('languages.addDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Globe className="h-4 w-4 text-slate-500" />
                {t('languages.keyName')} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={addDialog.key}
                onChange={(e) => setAddDialog(prev => ({ ...prev, key: e.target.value }))}
                placeholder={t('languages.keyPlaceholder')}
                className="font-mono text-sm"
              />
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">{t('languages.translationValues')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {languages.map(lang => (
                  <div key={lang.code}>
                    <Label className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{lang.flag}</span>
                      {lang.name}
                    </Label>
                    <Input
                      value={addDialog.values[lang.code] || ''}
                      onChange={(e) => setAddDialog(prev => ({
                        ...prev,
                        values: { ...prev.values, [lang.code]: e.target.value }
                      }))}
                      placeholder={`${lang.name} ${t('languages.translationPlaceholder')}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog({ open: false, key: '', values: { tr: '', en: '', nl: '', fr: '' } })} disabled={isSaving}>
              {t('languages.cancel')}
            </Button>
            <Button onClick={addNewTranslation} className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {isSaving ? t('languages.adding') : t('languages.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Analysis Dialog */}
      <Dialog open={importDialog.open} onOpenChange={(v) => !v && setImportDialog({ open: false, analysis: null, file: null, mode: 'merge' })}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileJson className="h-5 w-5 text-blue-600" />
              {t('languages.importPreviewTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('languages.importPreviewDesc')}
            </DialogDescription>
          </DialogHeader>
          
          {importDialog.analysis && (
            <div className="space-y-4 py-4 flex-1 overflow-hidden flex flex-col">
              {/* Stats Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{importDialog.analysis.will_add}</p>
                    <p className="text-sm text-emerald-700">{t('languages.willAdd')}</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{importDialog.analysis.will_update}</p>
                    <p className="text-sm text-amber-700">{t('languages.willUpdate')}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-slate-600">{importDialog.analysis.total}</p>
                    <p className="text-sm text-slate-700">{t('languages.totalKeys')}</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Import Mode */}
              <div className="flex items-center gap-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <Label className="font-medium">{t('languages.importMode')}</Label>
                <Select value={importDialog.mode} onValueChange={(v) => setImportDialog(prev => ({ ...prev, mode: v }))}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="merge">{t('languages.modeMerge')}</SelectItem>
                    <SelectItem value="skip">{t('languages.modeSkip')}</SelectItem>
                    <SelectItem value="overwrite">{t('languages.modeOverwrite')}</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-500">
                  {importDialog.mode === 'merge' && t('languages.modeMergeDesc')}
                  {importDialog.mode === 'skip' && t('languages.modeSkipDesc')}
                  {importDialog.mode === 'overwrite' && t('languages.modeOverwriteDesc')}
                </span>
              </div>
              
              {/* Warning for existing keys */}
              {importDialog.analysis.will_update > 0 && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      {importDialog.analysis.will_update} {t('languages.keysAlreadyExist')}
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {t('languages.keysAlreadyExistDesc')} "{importDialog.mode === 'skip' ? t('languages.modeSkip') : t('languages.modeMerge')}" {t('languages.protectExisting')}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Preview Lists */}
              <div className="flex-1 overflow-auto space-y-4">
                {importDialog.analysis.new_keys.length > 0 && (
                  <div>
                    <h4 className="font-medium text-emerald-600 mb-2 flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      {t('languages.newKeysToAdd')} ({importDialog.analysis.new_keys.length})
                    </h4>
                    <div className="max-h-[150px] overflow-auto border rounded-lg">
                      <Table>
                        <TableBody>
                          {importDialog.analysis.new_keys.slice(0, 20).map(item => (
                            <TableRow key={item.key} className="text-xs">
                              <TableCell className="font-mono py-1">{item.key}</TableCell>
                              <TableCell className="py-1 text-slate-500">
                                {Object.entries(item.values).map(([lang, val]) => (
                                  <span key={lang} className="mr-2">{lang}: {val?.substring(0, 30)}{val?.length > 30 ? '...' : ''}</span>
                                ))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {importDialog.analysis.new_keys.length > 20 && (
                        <p className="text-xs text-slate-500 p-2 text-center">
                          {t('languages.andMoreKeys').replace('{count}', importDialog.analysis.new_keys.length - 20)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {importDialog.analysis.existing_keys.length > 0 && (
                  <div>
                    <h4 className="font-medium text-amber-600 mb-2 flex items-center gap-2">
                      <Edit className="h-4 w-4" />
                      {t('languages.keysToUpdate')} ({importDialog.analysis.existing_keys.length})
                    </h4>
                    <div className="max-h-[150px] overflow-auto border rounded-lg">
                      <Table>
                        <TableBody>
                          {importDialog.analysis.existing_keys.slice(0, 20).map(item => (
                            <TableRow key={item.key} className="text-xs">
                              <TableCell className="font-mono py-1">{item.key}</TableCell>
                              <TableCell className="py-1 text-slate-500">{t('languages.existingToNew')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {importDialog.analysis.existing_keys.length > 20 && (
                        <p className="text-xs text-slate-500 p-2 text-center">
                          {t('languages.andMoreKeys').replace('{count}', importDialog.analysis.existing_keys.length - 20)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setImportDialog({ open: false, analysis: null, file: null, mode: 'merge' })} disabled={isImporting}>
              {t('languages.cancel')}
            </Button>
            <Button onClick={executeImport} className="gap-2 bg-blue-600 hover:bg-blue-700" disabled={isImporting}>
              {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {isImporting ? t('languages.importing') : t('languages.import')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync to DB Dialog */}
      <Dialog open={syncDialog.open} onOpenChange={(v) => !v && setSyncDialog({ open: false, status: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-blue-600" />
              {t('languages.syncTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('languages.syncDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Current Status */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-700 dark:text-slate-300">
                  {flattenedTranslations.length}
                </p>
                <p className="text-xs text-slate-500">{t('languages.inI18n')}</p>
              </div>
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {syncStatus?.total_in_db || 0}
                </p>
                <p className="text-xs text-blue-600">{t('languages.inDb')}</p>
              </div>
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-600">
                  {Math.max(0, flattenedTranslations.length - (syncStatus?.total_in_db || 0))}
                </p>
                <p className="text-xs text-amber-600">{t('languages.toBeAdded')}</p>
              </div>
            </div>
            
            {/* Explanation */}
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                {t('languages.whatDoesThisDo')}
              </p>
              <ul className="text-xs text-green-700 dark:text-green-300 space-y-1 list-disc list-inside">
                <li>{t('languages.syncInfo1')}</li>
                <li>{t('languages.syncInfo2')}</li>
                <li>{t('languages.syncInfo3')}</li>
                <li>{t('languages.syncInfo4')}</li>
              </ul>
            </div>
            
            {syncStatus?.is_synced && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    {t('languages.dbSynced')}
                  </p>
                  <p className="text-xs text-emerald-600">
                    {t('languages.dbSyncedDesc').replace('{synced}', syncStatus.synced_from_i18n).replace('{added}', syncStatus.user_added)}
                  </p>
                </div>
              </div>
            )}
            
            {!syncStatus?.is_synced && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    {t('languages.dbNotSynced')}
                  </p>
                  <p className="text-xs text-amber-600">
                    {t('languages.dbNotSyncedDesc')}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncDialog({ open: false, status: null })} disabled={isSyncing}>
              {t('languages.cancel')}
            </Button>
            <Button 
              onClick={handleSync} 
              className="gap-2 bg-blue-600 hover:bg-blue-700" 
              disabled={isSyncing}
            >
              {isSyncing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('languages.syncing')}
                </>
              ) : (
                <>
                  <Database className="h-4 w-4" />
                  {t('languages.syncBtn')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Languages;
